import { z } from "zod";
import { InductionLocaleSchema } from "./induction-locale";
import { EventTimeSourceSchema } from "./event-time-provenance";

const InductionDisplayViewWireSchema = z.object({
  heading: z.string(),
  title: z.string(),
  address: z.string().nullable(),
  groups: z.array(z.object({
    key: z.string(),
    label: z.string(),
    value: z.string().nullable(),
    omitted: z.boolean(),
    language: z.string(),
    origin: z.enum(["source", "ai_translation", "builder_translation"]),
  }).passthrough()),
  acknowledgementStatement: z.string(),
  acknowledgeButton: z.string(),
  notice: z.string().nullable(),
  viewDigest: z.string(),
}).passthrough();

const InductionDisplaySourceWireSchema = z.object({
  title: z.string(),
  address: z.string().nullable(),
  groups: z.array(z.object({
    key: z.string(),
    label: z.string(),
    value: z.string().nullable(),
    omitted: z.boolean(),
  }).passthrough()),
  sourceDigest: z.string(),
}).passthrough();

/** Opaque server-signed proof. Preserve additive fields at every signed level
 * so older clients echo the complete payload without invalidating its signature. */
export const InductionDisplayDescriptorWireSchema = z.object({
  // View/source fields are optional so older signed v1 descriptors remain
  // readable; clients require them before presenting or acknowledging a view.
  payload: z.object({
    v: z.literal(1),
    siteFieldsHash: z.string().optional(),
    selectedLocale: z.string().optional(),
    source: InductionDisplaySourceWireSchema.optional(),
    view: InductionDisplayViewWireSchema.optional(),
  }).passthrough(),
  signature: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
}).passthrough();
export type InductionDisplayDescriptorWire = z.infer<
  typeof InductionDisplayDescriptorWireSchema
>;

export const SiteInductionAcknowledgementSchema = z
  .object({
    projectId: z.string(),
    siteFieldsHash: z.string(),
    acknowledgedAt: z.string().datetime(),
    acknowledgedReceivedAt: z.string().datetime().nullable().optional(),
    acknowledgedTimeSource: EventTimeSourceSchema.nullable().optional(),
    // Language the worker was shown at acknowledgement time (issue #991).
    // Absent/null on legacy rows and English acknowledgements.
    locale: InductionLocaleSchema.nullish(),
  })
  .strict();

export type SiteInductionAcknowledgement = z.infer<
  typeof SiteInductionAcknowledgementSchema
>;
